///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { GtthNotificationMessage } from './gtth-notification-message';
import { _remove } from '../../../shared/utils';
import { SuiTranslateService } from '@siklu/sui-lib/localization';

@Injectable({
    providedIn: 'root'
})
export class GtthNotificationsService {
    messagesCounter = 0;
    messagesStack: GtthNotificationMessage[];
    private readonly NG_HTTP_FAILURE_MESSAGE = 'Http failure response for';
    private readonly maxStackLength = 5;
    private readonly successTimeToLive = -1;
    private readonly errorTimeToLive = -1;
    private readonly warningTimeToLive = -1;
    private readonly subscribersMap: Map<number, Subscription>;

    constructor(private readonly translateService: SuiTranslateService) {
        this.messagesStack = [];
        this.subscribersMap = new Map();
    }

    showSuccess(message: string, timeToLive?: number) {
        if (message && message !== '') {
            this.showMessage(message, 'alert-success', timeToLive || this.successTimeToLive);
        }
    }

    showError(message: string, timeToLive?: number) {
        if (message && message !== '') {
            this.showMessage(message, 'alert-danger', timeToLive || this.errorTimeToLive);
        }
    }

    showWarning(message: string, timeToLive?: number) {
        if (message && message !== '') {
            this.showMessage(message, 'alert-warning', timeToLive || this.warningTimeToLive);
        }
    }

    showHttpError(error: any) {
        if (error.description) {
            this.showError(error.description);
        } else if (error?.error?.description) {
            this.showError(error.error.description);
        } else if (error.message) {
            if (error.params) {
                const translation = this.translateService.translate('error.' + error.message, error.params);
                this.showError(translation);
            } else if (error.message.startsWith(this.NG_HTTP_FAILURE_MESSAGE)) {
                this.showError('Internal server error');
            } else {
                const translation = this.translateService.translate('error.' + error.message);
                this.showError(translation);
            }
        } else {
            const errorText = error._body;
            this.showError(errorText);
        }
    }

    public hideMessage(messageId: number) {
        _remove(this.messagesStack, { id: messageId });

        if (this.subscribersMap.has(messageId)) {
            this.subscribersMap.get(messageId).unsubscribe();
            this.subscribersMap.delete(messageId);
        }

        if (this.messagesStack.length === 0) {
            this.messagesCounter = 0;
        }
    }

    clearAllMessages() {
        if (this.messagesStack) {
            this.messagesStack.forEach((message: GtthNotificationMessage) => {
                if (message) {
                    this.hideMessage(message.id);
                }
            });
        }

        this.messagesStack = [];
    }

    private showMessage(message: string, styleClass: string, ttl: number) {
        if (this.messagesStack.find((messageObject) => messageObject.text === message)) {
            return;
        }

        const messageId = this.messagesCounter;
        this.messagesCounter++;
        const notification = new GtthNotificationMessage(messageId, message, styleClass);

        if (this.messagesStack.length >= this.maxStackLength) {
            this.hideMessage(this.messagesStack[0].id);
        }
        this.messagesStack.push(notification);

        if (ttl > 0) {
            this.subscribersMap.set(
                notification.id,
                timer(ttl).subscribe(() => this.hideMessage(notification.id))
            );
        }
    }
}
